import { useEffect, useMemo, useRef } from "react";

import { TemplateProvider, useTemplate } from "@/context/TemplateContext";
import { CartProvider } from "@/context/CartContext";

import EcomHeader from "@/pages/template-ecommerce/components/Header";
import EcomFooter from "@/pages/template-ecommerce/components/Footer";
import EcomHome from "@/pages/template-ecommerce/Home";
import "@/pages/template-ecommerce/ecommerce-theme.css";

import ServiceHeader from "@/template-service/components/Header";
import ServiceFooter from "@/template-service/components/Footer";
import ServiceHome from "@/template-service/pages/Home";
import { ServiceBasePathProvider } from "@/template-service/lib/route";
import { setServiceData } from "@/template-service/lib/_data";

import RestaurantLanding from "@/template-restaurant/pages/Landing";
import { setRestaurantData } from "@/template-restaurant/lib/_data";
import "@/template-restaurant/restaurant.css";

/**
 * SrcPreviewFrame — read-only WebsiteTab preview that renders the exact same
 * SRC template family the public site uses, driven off the compiled
 * `service_config` / `restaurant_config` / `ecommerce_config` payload the
 * backend already builds from `website_content`.
 *
 * Design constraints (Iter 69):
 *  - Never navigate the outer dashboard router. All <a>, <button>, and form
 *    submits inside the preview are intercepted at capture-phase.
 *  - Never mount a nested react-router (a BrowserRouter is already in the
 *    tree). We use the same `ServiceBasePathProvider` + `TemplateProvider`
 *    pattern EditableCanvas uses to feed basePath/context by prop.
 *  - Never mutate WebsiteTab form/save behaviour. The parent passes a
 *    fresh `payload` after each save round-trip.
 *  - Only renders the three SRC families; returns null otherwise.
 */
export default function SrcPreviewFrame({ payload, template, slug }) {
  const containerRef = useRef(null);

  // Capture-phase interceptor — blocks any interaction that would navigate
  // the dashboard, submit a form, or side-effect the cart. Scroll/hover
  // still work because those aren't in this list.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const swallow = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest("a[href], button, [role='button'], form")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const swallowSubmit = (e) => { e.preventDefault(); e.stopPropagation(); };
    el.addEventListener("click", swallow, true);
    el.addEventListener("submit", swallowSubmit, true);
    return () => {
      el.removeEventListener("click", swallow, true);
      el.removeEventListener("submit", swallowSubmit, true);
    };
  }, []);

  if (!payload || !template || !["service", "restaurant", "ecommerce"].includes(template)) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="src-preview-frame"
      data-testid="src-preview-frame"
      data-preview-template={template}
      style={{
        // Force this element to be the containing block for any
        // `position: fixed` descendants (SRC ecommerce Header uses
        // `fixed top-0 z-50`). Without this the SRC header escapes the
        // preview scroll container and overlays the dashboard chrome.
        // `transform` creates a new containing block per CSS spec — same
        // trick EditableCanvas uses.
        transform: "translateZ(0)",
      }}
    >
      {template === "service"    && <ServicePreview    payload={payload} slug={slug} />}
      {template === "restaurant" && <RestaurantPreview payload={payload} slug={slug} />}
      {template === "ecommerce"  && <EcommercePreview  payload={payload} slug={slug} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Service                                                                    */
/* -------------------------------------------------------------------------- */

function ServicePreview({ payload, slug }) {
  useMemo(() => {
    if (payload?.service_config) setServiceData(payload.service_config);
  }, [payload?.service_config]);
  return (
    <ServiceBasePathProvider basePath={`/site/${slug}`}>
      <div
        className="min-h-screen bg-[#f9f6f0] text-[#2c2a29] font-body"
        data-testid="src-preview-service"
      >
        <ServiceHeader />
        <main>
          <ServiceHome />
        </main>
        <ServiceFooter />
      </div>
    </ServiceBasePathProvider>
  );
}

/* -------------------------------------------------------------------------- */
/* Restaurant                                                                 */
/* -------------------------------------------------------------------------- */

function RestaurantPreview({ payload, slug }) {
  useMemo(() => {
    if (payload?.restaurant_config) setRestaurantData(payload.restaurant_config);
  }, [payload?.restaurant_config]);
  return (
    <div className="restaurant-template" data-testid="src-preview-restaurant">
      <RestaurantLanding slug={slug} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Ecommerce                                                                  */
/* -------------------------------------------------------------------------- */

function EcommercePreview({ payload, slug }) {
  const basePath = `/site/${slug}`;
  return (
    <TemplateProvider
      industryKey={null}
      overrides={payload?.ecommerce_config || {}}
      basePath={basePath}
      slug={slug}
      checkoutEnabled={false}
      websiteContent={payload?.website_content || {}}
      summary={payload?.summary || {}}
    >
      <EcomChrome basePath={basePath} />
    </TemplateProvider>
  );
}

function EcomChrome({ basePath }) {
  const { config } = useTemplate();
  return (
    <CartProvider pricing={{}}>
      <div
        className="strvkt-theme min-h-screen flex flex-col"
        data-testid="src-preview-ecommerce"
      >
        <EcomHeader
          basePath={basePath}
          transparent={true}
          announcementMessages={config?.announcementMessages}
        />
        <main className="flex-1">
          <EcomHome />
        </main>
        <EcomFooter basePath={basePath} />
      </div>
    </CartProvider>
  );
}
